<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useOptionsStore } from '@/stores/options';
import { useSupportPlanCriteriaStore } from '@/stores/supportPlanCriteria';

const emit = defineEmits(['close-modal']);

const { storeSupportPlanCriteria, updateSupportPlanCriteria } = useSupportPlanCriteriaStore();
const { supportPlanCriteriaTypes } = storeToRefs(useOptionsStore());

const props = defineProps({
    criteria: {
        type: Object,
        default: () => ({})
    },
    supportPlan: {
        type: Object,
        required: true
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const form = ref({
    ...props.criteria,
    support_plan_id: props.supportPlan.id,
    type_id: props.criteria.type?.id ?? null,
    defined_options: props.criteria?.defined_options ?? [],
});

watch(() => props.criteria, (newValue) => {
    form.value.type = newValue.type?.id ?? null;
}, { immediate: true });

const isDefinedType = computed(() => {
    const typeValue = typeof form.value.type === 'object' ? form.value.type?.value : form.value.type;
    return typeValue === supportPlanCriteriaTypes.value.find(t => t.label === 'Defined Criteria')?.value;
});

const isMeasurableType = computed(() => {
    const typeValue = typeof form.value.type === 'object' ? form.value.type?.value : form.value.type;
    return typeValue === supportPlanCriteriaTypes.value.find(t => t.label === 'Measurable Criteria')?.value;
});

const addDefinedOption = () => {
    form.value.defined_options.push({ defined_criteria_name: null });
};

const removeDefinedOption = (index) => {
    form.value.defined_options.splice(index, 1);
};

const save = async () => {
    const action = props.mode === 'Create' ? storeSupportPlanCriteria : updateSupportPlanCriteria;

    await action({
        ...form.value,
        type_id: typeof form.value.type === 'object' ? form.value.type?.value : form.value.type,
        defined_options: isDefinedType.value ? form.value.defined_options : []
    });

    emit('close-modal');
};

const cancel = () => {
    emit('close-modal');
};

</script>

<template>
<ModalWrapper>
    <template #header>{{ $t('label.modal_title', { mode: mode, what: 'Criteria'}) }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { what: 'the criteria of current support plan'}) }}</template>

    <form class="grid grid-col-1 md:grid-cols-2 gap-4" @submit.prevent="save">
        <StyledDropdown
            v-model="form.type"
            name="type_id"
            :label="$t('label.criteria_type')"
            :options="supportPlanCriteriaTypes" />
        <StyledInput
            v-model="form.title"
            :placeholder="$t('label.title')"
            name="title"
            :label="$t('label.title')" />
        <StyledInput
            v-model="form.description"
            class="col-span-full"
            :placeholder="$t('label.criteria_description')"
            name="description"
            :label="$t('label.description')" />
        <template v-if="isMeasurableType">
            <StyledInput
                v-model="form.measurable_unit_name"
                :placeholder="$t('label.unit_name')"
                name="measurable_unit_name"
                :label="$t('label.unit_name')" />
            <StyledInput
                v-model="form.measurable_unit_target"
                :placeholder="$t('label.target_value')"
                name="measurable_unit_target"
                :label="$t('label.target_value')" />
        </template>
        <template v-if="isDefinedType">
            <div>
                <label class="block font-medium mb-2">{{ $t('label.defined_options') }}</label>
                <div v-for="(option, index) in form.defined_options" :key="index" class="flex items-center gap-2 mb-2">
                    <StyledInput
                        v-model="option.defined_criteria_name"
                        :placeholder="$t('label.option_name')"
                        :name="`defined_options.${index}.defined_criteria_name`" />
                    <v-icon 
                        v-if="form.defined_options.length > 1"
                        class="self-center pb-5"
                        icon="mdi-trash-can-outline" 
                        color="red"
                        @click="removeDefinedOption(index)" />
                </div>
                <StyledButton class="text-blue-600" @click="addDefinedOption">
                    <v-icon icon="mdi-plus-circle" color="white" class="mr-1" />
                    {{ $t('label.add_option') }}
                </StyledButton>
            </div>
        </template>
    </form>
    <template #footer>
        <StyledSaveButton @save="save" @cancel="cancel" />
    </template>
</ModalWrapper>
</template>
